/**
 * One reorderable sidebar list.
 *
 * `reorder.ts` owns the arithmetic and the storage; this owns the two ways a
 * human moves a row, and hands each row the props that make it movable.
 *
 * ## Two affordances, deliberately
 *
 * **Drag** is what the design asks for and what a pointer user reaches for.
 * **Alt+ArrowUp / Alt+ArrowDown on the focused row** is the same operation
 * for everybody else. A drag-only sidebar is unusable without a pointer —
 * HTML5 drag-and-drop emits nothing for a keyboard and nothing a screen
 * reader can drive — so the keyboard path is not a fallback bolted on
 * afterwards, it is the primary one this module can actually promise.
 *
 * `Alt` rather than a bare arrow: the rows are links inside a scrolling
 * region, where bare arrows scroll and are how assistive technology moves
 * between them. `Alt+Left/Right` is the browser's back/forward, which is why
 * this is Up/Down. Each row advertises the shortcut with `aria-keyshortcuts`.
 *
 * ## The order that gets written down is the RENDERED one
 *
 * Not the stored list with an edit applied to it. The rendered order is by
 * construction a permutation of what actually exists (`applyOrder`'s
 * invariant), so every write prunes the ids that have gone rather than
 * carrying a deleted project's id forward for the life of the browser
 * profile.
 */

import { useCallback, useRef, useState } from "react";
import { applyOrder, moveWithin, readOrder, writeOrder } from "./reorder";

/** Spread onto the row element. Nothing here paints; the row keeps its own
 *  chrome. */
export interface ReorderRowProps {
  "data-reorder-id": string;
  "aria-keyshortcuts": string;
  draggable: true;
  onDragStart: (event: { dataTransfer?: DataTransfer | null }) => void;
  onDragOver: (event: { preventDefault: () => void }) => void;
  onDrop: (event: { preventDefault: () => void; stopPropagation: () => void }) => void;
  onDragEnd: () => void;
  onKeyDown: (event: {
    key: string;
    altKey: boolean;
    preventDefault: () => void;
  }) => void;
}

export interface Reordered<T> {
  /** `items`, in the order this viewer put them. */
  ordered: T[];
  /** The props that make one row movable. */
  rowProps: (id: string) => ReorderRowProps;
}

/** What each row tells the user its keys are. */
export const REORDER_SHORTCUTS = "Alt+ArrowUp Alt+ArrowDown";

export function useReorder<T>(
  storageKey: string,
  items: readonly T[],
  idOf: (item: T) => string,
): Reordered<T> {
  // Read once, on mount. Re-reading on every render would fight the user's
  // own edits, and nothing else in this tab writes the key.
  const [stored, setStored] = useState<string[]>(() => readOrder(storageKey));
  /** The row a drag is currently carrying. A ref, not state: it changes
   *  several times per second during a drag and paints nothing. */
  const dragging = useRef<string | null>(null);

  const ordered = applyOrder(items, idOf, stored);
  /** Read inside the callbacks, which must not close over a stale render. */
  const orderedIds = useRef<string[]>([]);
  orderedIds.current = ordered.map(idOf);

  const move = useCallback(
    (id: string, to: number | string) => {
      const next = moveWithin(orderedIds.current, id, to);
      setStored(next);
      writeOrder(storageKey, next);
    },
    [storageKey],
  );

  const rowProps = useCallback(
    (id: string): ReorderRowProps => ({
      "data-reorder-id": id,
      "aria-keyshortcuts": REORDER_SHORTCUTS,
      draggable: true,
      onDragStart: (event) => {
        dragging.current = id;
        // Firefox refuses to start a drag without payload; every other
        // browser ignores it. Optional because a synthetic drag in a test
        // has none, and this must not be the thing that throws.
        try {
          event.dataTransfer?.setData("text/plain", id);
        } catch {
          // A transfer object that refuses a write is not a reason to
          // abandon the drag — `dragging` above already holds what matters.
        }
      },
      // Without this the drop event never fires: the browser's default for
      // dragover is "this is not a drop target".
      onDragOver: (event) => event.preventDefault(),
      onDrop: (event) => {
        event.preventDefault();
        event.stopPropagation();
        const from = dragging.current;
        dragging.current = null;
        if (from && from !== id) move(from, id);
      },
      onDragEnd: () => {
        dragging.current = null;
      },
      onKeyDown: (event) => {
        if (!event.altKey) return;
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
        // The region scrolls; an arrow that both moved the row and scrolled
        // the list would leave the moved row somewhere other than under the
        // cursor that moved it.
        event.preventDefault();
        move(id, event.key === "ArrowUp" ? -1 : 1);
      },
    }),
    [move],
  );

  return { ordered, rowProps };
}
